import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store';
import { useAppSelector } from '../../../hooks/hooksRedux';
import { Card } from '../../../components/atoms';
import { FileViewer } from './FileViewer';
import { FolderService } from '../../../services/FolderService';
import { AxiosInstance } from 'axios';
import { useApi } from '../../../services/hooks/apiRedux';
import { useAuth } from '../../../store/auth';
import { errorToast } from '../../../utils/toasts';
import { errorMessageAdapter } from '../../../adapters';
import { ICompanyFolders } from '../../../services/Interfaces/IFolderService';

export interface FileItem {
  id: number;
  name: string;
  uploadedAt: string;
  signed: boolean;
  url: string;
}

const folderOrder: string[] = [
  'incorporation',
  'certificates',
  'changes',
  'forms',
  'accounting',
  'otherdocuments'
];

interface TabsDocumentsProps {
  companyId: string | number | undefined;
}

type FolderTab = { id: string; name: string };

const TabsDocuments = ({ companyId }: TabsDocumentsProps) => {
  const [activeTab, setActiveTab] = useState<string>('');
  const [tabs, setTabs] = useState<{ id: string; name: string }[]>([]);

  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);
  const { t, i18n } = useTranslation();
  const api: AxiosInstance = useApi();
  const { token } = useAuth();

  const MAX_FILE_SIZE_MB = 2;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
  const normalize = (name: string) => name.toLowerCase().replace(/\s/g, '');

  const lastFetchedCompanyIdRef = useRef<string | number | undefined>();

  const getFolders = useCallback(
    async (force = false) => {
      try {
        if (!companyId) {
          return;
        }

        if (!force && lastFetchedCompanyIdRef.current === companyId) {
          return;
        }

        lastFetchedCompanyIdRef.current = companyId;

        const foldersResponse: ICompanyFolders = await FolderService.getCompanyFolders(api, companyId, {
          headers: {
            'Content-Language': i18n.language.toLowerCase(),
            Authorization: `Bearer ${token}`
          }
        });

        if (foldersResponse) {
          const folderList = foldersResponse.folders
            .map((folder: { id: string; name: string }) => ({
              id: folder.id,
              name: folder.name
            }))
            .sort((a: FolderTab, b: FolderTab) => {
              const indexA = folderOrder.indexOf(normalize(a.name));
              const indexB = folderOrder.indexOf(normalize(b.name));

              const safeIndexA = indexA === -1 ? Infinity : indexA;
              const safeIndexB = indexB === -1 ? Infinity : indexB;

              return safeIndexA - safeIndexB;
            });

          setTabs(folderList);
        }
      } catch (e: any) {
        errorToast(errorMessageAdapter(e));
      }
    },
    [api, companyId, i18n.language, token]
  );

  useEffect(() => {
    void getFolders();
  }, [getFolders]);

  useEffect(() => {
    if (tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs]);

  const renderTabComponent = () => {
    if (!activeTab) return null;

    return (
      <FileViewer
        folderId={activeTab}
        companySlug={companyId!}
        onDrop={async (newFiles: File[]) => {
          for (const file of newFiles) {
            if (file.size > MAX_FILE_SIZE_BYTES) {
              errorToast(`O arquivo "${file.name}" excede o limite de ${MAX_FILE_SIZE_MB}MB.`);
              continue;
            }

            const formPayload = {
              folder_id: activeTab,
              document: file,
              agent_can_view: true,
              agent_can_manage: false
            };

            try {
              await FolderService.customerUploadDocument(api, formPayload, {
                headers: {
                  'Content-Language': i18n.language.toLowerCase(),
                  Authorization: `Bearer ${token}`
                }
              });
              lastFetchedCompanyIdRef.current = undefined;
              void getFolders(true);
            } catch (e: any) {
              errorToast(errorMessageAdapter(e));
            }
          }
        }}
      />
    );
  };

  return (
    <Card.Root className="mt-4 w-full min-w-0 overflow-hidden">
      <Card.Header>
        <h3 className="text-lg font-medium">{t('others.othDocuments')}</h3>
      </Card.Header>

      <Card.Content className="min-w-0 w-full overflow-hidden">
        <div className="flex w-full min-w-0 flex-col">
          <div className="mb-4 flex gap-4 overflow-x-auto border-b pb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="shrink-0 whitespace-nowrap pb-2 font-medium"
                style={{
                  color: activeTab === tab.id ? colors.primary : '#4b5563',
                  borderBottom: activeTab === tab.id ? `2px solid ${colors.primary}` : '2px solid transparent'
                }}
              >
                {/* {tab.name} */}
                {t(`certificatesDocuments.${tab.name}`)}
              </button>
            ))}
          </div>

          <div className="pt-4">
            {renderTabComponent()}
          </div>
        </div>
      </Card.Content>
    </Card.Root>
  );
};

export { TabsDocuments };
